import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Loader2, FileText, BookOpen,
  Heart, Palette, ShoppingBag, Wand2
} from "lucide-react";
import api from "../lib/api";

const CONTENT_TYPES = [
  { id: "ai_template", label: "AI Template", icon: FileText, desc: "Reusable AI prompt templates" },
  { id: "content_pack", label: "Content Pack", icon: BookOpen, desc: "Curated content collections" },
  { id: "ecard_pack", label: "E-Card Pack", icon: Heart, desc: "Greeting cards and messages" },
  { id: "prompt_pack", label: "Prompt Pack", icon: Sparkles, desc: "Sets of AI prompts" },
  { id: "guide", label: "Guide", icon: BookOpen, desc: "In-depth guides and tutorials" },
  { id: "custom", label: "Custom", icon: Palette, desc: "Other digital content" },
];

const CATEGORIES = [
  "productivity", "creative", "business", "education",
  "entertainment", "lifestyle", "health", "technology",
];

export default function MarketplaceCreatePage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState("content_pack");
  const [category, setCategory] = useState("productivity");
  const [previewContent, setPreviewContent] = useState("");
  const [fullContent, setFullContent] = useState("");
  const [price, setPrice] = useState("");
  const [tags, setTags] = useState("");
  const [creating, setCreating] = useState(false);

  // AI generation
  const [aiTopic, setAiTopic] = useState("");
  const [generating, setGenerating] = useState(false);

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) return;
    setGenerating(true);
    try {
      const r = await api.post("/marketplace/ai-generate", { topic: aiTopic.trim(), type: contentType });
      const d = r.data;
      if (d.title) setTitle(d.title);
      if (d.description) setDescription(d.description);
      if (d.preview_content) setPreviewContent(d.preview_content);
      if (d.suggested_price !== undefined) setPrice(d.suggested_price === 0 ? "" : String(d.suggested_price));
      if (d.tags?.length) setTags(d.tags.join(", "));
      toast.success("AI generated your listing!");
    } catch {
      toast.error("AI generation failed — try again");
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      const parsedPrice = parseFloat(price) || 0;
      await api.post("/marketplace/items", {
        title: title.trim(),
        description: description.trim(),
        content_type: contentType,
        category,
        preview_content: previewContent.trim(),
        full_content: fullContent.trim(),
        price: parsedPrice,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Listing created!");
      navigate("/marketplace/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create listing");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="marketplace-create-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/marketplace")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="create-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <ShoppingBag className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-sm font-semibold text-foreground">Create Listing</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* AI Assist Card */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 rounded-xl p-5" data-testid="ai-assist-card">
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="w-4 h-4 text-cyan-400" />
            <h2 className="text-sm font-semibold text-foreground">AI-Assisted Creation</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Describe what you want to sell and our AI will generate a listing for you.</p>
          <div className="flex gap-2">
            <input
              data-testid="ai-topic-input"
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              placeholder="e.g. productivity prompts for remote workers"
              className="flex-1 px-3 py-2 bg-card border border-cyan-500/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25"
            />
            <Button
              onClick={handleAIGenerate}
              disabled={generating || !aiTopic.trim()}
              className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-4 h-9 rounded-lg disabled:opacity-40"
              data-testid="ai-generate-btn"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate</>}
            </Button>
          </div>
        </div>

        {/* Content Type */}
        <div className="bg-card border border-border/60 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3">Content Type</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CONTENT_TYPES.map((ct) => (
              <button
                key={ct.id}
                onClick={() => setContentType(ct.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left border cursor-pointer transition-all ${
                  contentType === ct.id
                    ? "border-cyan-400 bg-cyan-500/10 text-cyan-300"
                    : "border-border bg-card text-muted-foreground hover:border-border"
                }`}
                data-testid={`type-${ct.id}`}
              >
                <ct.icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-medium">{ct.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="bg-card border border-border/60 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Listing Details</h2>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Title *</label>
            <input data-testid="listing-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Give your listing a catchy title" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25" />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Description</label>
            <textarea data-testid="listing-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={3} placeholder="Describe what buyers will get" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground/80 mb-1">Category</label>
              <select data-testid="listing-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground/80 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 cursor-pointer capitalize">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground/80 mb-1">Price (USD)</label>
              <input data-testid="listing-price" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0 = Free" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Tags (comma-separated)</label>
            <input data-testid="listing-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="productivity, AI, prompts" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25" />
          </div>
        </div>

        {/* Content */}
        <div className="bg-card border border-border/60 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Content</h2>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Preview (visible to everyone)</label>
            <textarea data-testid="listing-preview" value={previewContent} onChange={(e) => setPreviewContent(e.target.value)} maxLength={1000} rows={3} placeholder="A teaser of your content — this is shown before purchase" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Full Content (unlocked after purchase)</label>
            <textarea data-testid="listing-full-content" value={fullContent} onChange={(e) => setFullContent(e.target.value)} rows={6} placeholder="The full premium content buyers will receive" className="w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none" />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between pb-8">
          <button onClick={() => navigate("/marketplace")} className="text-sm text-muted-foreground bg-transparent border-0 cursor-pointer hover:text-foreground/80">Cancel</button>
          <Button
            onClick={handleCreate}
            disabled={creating || !title.trim()}
            className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white px-6 h-10 rounded-lg text-sm font-medium disabled:opacity-40 press-down"
            data-testid="publish-listing-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
            {creating ? "Publishing..." : "Publish Listing"}
          </Button>
        </div>
      </div>
    </div>
  );
}
